import { useCallback, useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { FileAudio, Music, Upload, X } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Spinner,
} from '../../components/index.js';
import {
  isComplete,
  pollProgress,
  startTranscription,
  sttConfigured,
  type Progress,
  type Template,
} from '../../lib/stt-api.js';

const ALLOWED_EXT = ['.m4a', '.mp3', '.wav', '.mp4', '.webm', '.ogg', '.flac'];
const POLL_INTERVAL_MS = 1000;

type Phase = 'idle' | 'uploading' | 'polling' | 'handing-off' | 'error';

interface PendingFile {
  file: File;
  id: string;
}

function fmtMB(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i === -1 ? '' : name.slice(i).toLowerCase();
}

const TEMPLATE_OPTIONS: { value: Template; label: string }[] = [
  { value: 'going_concern', label: 'Going Concern' },
  { value: 'liquidation', label: 'Liquidation' },
];

// ---------------------------------------------------------------------------
// Hook — owns upload state + handlers, returns ready-to-place ReactNodes so
// callers can compose their own layout. Use directly when you need
// non-standard layout (e.g. dropzone at top, action row at the bottom of a
// larger card). For the standard layout, render <DictationUploader> instead.
// ---------------------------------------------------------------------------

export interface UseDictationFlowProps {
  template: Template;
  onComplete: (args: { sttTaskId: string; firstFileName: string }) => Promise<void> | void;
  onTemplateChange?: (template: Template) => void;
  ctaLabel?: string;
  compact?: boolean;
}

export interface DictationFlowSlots {
  dropzone: ReactNode;
  fileList: ReactNode | null;
  progress: ReactNode | null;
  error: ReactNode | null;
  templateToggle: ReactNode;
  transcribeButton: ReactNode;
  filesReadyText: string | null;
  /** Current internal state — exposed so callers can disable surrounding UI. */
  busy: boolean;
  hasFiles: boolean;
  configured: boolean;
}

export function useDictationFlow(props: UseDictationFlowProps): DictationFlowSlots {
  const { template, onComplete, onTemplateChange, ctaLabel, compact = false } = props;
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<Progress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const configured = sttConfigured();

  const reset = useCallback(() => {
    setFiles([]);
    setProgress(null);
    setError(null);
    setPhase('idle');
  }, []);

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const list = Array.from(incoming);
    setFiles((prev) => {
      const seen = new Set(prev.map((p) => p.file.name));
      const next = [...prev];
      for (const f of list) {
        if (!ALLOWED_EXT.includes(extOf(f.name))) continue;
        if (seen.has(f.name)) continue;
        seen.add(f.name);
        next.push({ file: f, id: `${f.name}-${f.size}-${f.lastModified}` });
      }
      next.sort((a, b) => a.file.name.localeCompare(b.file.name));
      return next;
    });
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const onTranscribe = useCallback(async () => {
    if (files.length === 0) return;
    setPhase('uploading');
    setError(null);
    setProgress({ task_id: '', status: 'uploading', progress: 0, stage: 'Uploading…' });

    try {
      const startRes = await startTranscription(template, files.map((f) => f.file));
      const id = startRes.task_id;
      if (!id) throw new Error('No task ID returned from STT service');
      setPhase('polling');

      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const p = await pollProgress(id);
        setProgress(p);
        if (isComplete(p.status)) {
          setPhase('handing-off');
          try {
            await onComplete({ sttTaskId: id, firstFileName: files[0]!.file.name });
            reset();
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            setError(`Transcription complete but handoff failed: ${msg}`);
            setPhase('error');
          }
          return;
        }
        if (p.status === 'error') {
          // Server should always populate p.error, but if it doesn't (blank
          // exception text reaching here would mean an upstream regression),
          // include the stage so the user has something actionable.
          const detail = (p.error ?? '').trim();
          throw new Error(detail || `Pipeline error during "${p.stage ?? 'unknown stage'}" — check engine logs.`);
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setPhase('error');
    }
  }, [files, template, onComplete, reset]);

  const busy = phase === 'uploading' || phase === 'polling' || phase === 'handing-off';
  const canSubmit = files.length > 0 && configured && !busy;
  const hasFiles = files.length > 0;

  const dropzone = (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      className={[
        'flex flex-col items-center justify-center gap-1.5 text-center',
        'rounded-lg border-2 border-dashed cursor-pointer transition-colors',
        compact ? 'px-4 py-5' : 'px-6 py-10',
        dragOver
          ? 'border-accent-base bg-accent-muted'
          : 'border-border-subtle hover:border-accent-base hover:bg-bg-surface-2',
      ].join(' ')}
      role="button"
      tabIndex={0}
      aria-label="Upload audio files"
    >
      <Upload className={compact ? 'size-5 text-accent-base' : 'size-6 text-accent-base'} aria-hidden />
      <div className={compact ? 'text-sm text-text-primary' : 'text-text-primary font-medium'}>
        {ctaLabel ?? 'Drop a recording'}
      </div>
      <div className="text-xs text-text-secondary">
        or click to browse — {ALLOWED_EXT.join(', ')}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_EXT.join(',')}
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) addFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );

  const fileList = files.length > 0 ? (
    <ul className="space-y-1.5">
      {files.map((f) => (
        <li
          key={f.id}
          className="flex items-center gap-3 rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-1.5"
        >
          <Music className="size-4 text-accent-base shrink-0" aria-hidden />
          <span className="flex-1 truncate text-sm text-text-primary">{f.file.name}</span>
          <span className="text-xs text-text-secondary tabular-nums">{fmtMB(f.file.size)}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeFile(f.id);
            }}
            disabled={busy}
            className="text-text-secondary hover:text-danger disabled:opacity-40"
            aria-label={`Remove ${f.file.name}`}
          >
            <X className="size-4" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  ) : null;

  const progressNode = busy && progress ? (
    <div>
      <div className="flex items-center justify-between mb-1 text-xs">
        <div className="flex items-center gap-2 text-text-secondary">
          <Spinner size="sm" />
          <span>
            {phase === 'handing-off' ? 'Attaching…' : progress.stage || 'Working…'}
          </span>
        </div>
        <span className="text-accent-base tabular-nums font-semibold">
          {phase === 'handing-off' ? '100%' : `${Math.max(0, Math.min(100, progress.progress))}%`}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-surface-2">
        <div
          className="h-full bg-accent-base transition-[width] duration-300"
          style={{
            width: `${phase === 'handing-off' ? 100 : Math.max(0, Math.min(100, progress.progress))}%`,
          }}
        />
      </div>
    </div>
  ) : null;

  const errorNode = phase === 'error' && error ? (
    <div role="alert" className="text-sm text-danger">
      {error}{' '}
      <button type="button" onClick={reset} className="underline hover:text-danger/80">
        Reset
      </button>
    </div>
  ) : null;

  const templateToggle = onTemplateChange ? (
    <div
      className="inline-flex rounded-md border border-border-subtle bg-bg-surface-2 p-0.5"
      role="group"
      aria-label="Template"
    >
      {TEMPLATE_OPTIONS.map((opt) => {
        const active = template === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={busy}
            onClick={() => onTemplateChange(opt.value)}
            className={[
              'px-2.5 py-1 text-xs rounded transition-colors disabled:opacity-50',
              active
                ? 'bg-accent-base text-text-inverse'
                : 'text-text-secondary hover:text-text-primary',
            ].join(' ')}
            aria-pressed={active}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  ) : (
    <span className="text-xs text-text-secondary">Template: {template}</span>
  );

  const transcribeButton = (
    <Button
      type="button"
      size={compact ? 'sm' : 'md'}
      disabled={!canSubmit}
      loading={busy}
      onClick={onTranscribe}
      leftIcon={<FileAudio className="size-4" aria-hidden />}
    >
      Transcribe
    </Button>
  );

  const filesReadyText =
    files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'} ready` : null;

  return {
    dropzone,
    fileList,
    progress: progressNode,
    error: errorNode,
    templateToggle,
    transcribeButton,
    filesReadyText,
    busy,
    hasFiles,
    configured,
  };
}

// ---------------------------------------------------------------------------
// Default-layout wrapper — for callers that don't need a custom layout.
// ---------------------------------------------------------------------------

export interface DictationUploaderProps extends UseDictationFlowProps {
  /** Wrap in a Card. */
  framed?: boolean;
  /** Title for the framed Card variant. */
  title?: string;
}

export function DictationUploader({
  framed = false,
  title,
  ...flowProps
}: DictationUploaderProps) {
  const flow = useDictationFlow(flowProps);

  const body = (
    <div className={flowProps.compact ? 'space-y-3' : 'space-y-4'}>
      {!flow.configured && (
        <div role="alert" className="text-sm text-warning">
          STT service URL not configured. Set <code className="font-mono">VITE_STT_API_URL</code>.
        </div>
      )}
      {flow.dropzone}
      {flow.fileList}
      {flow.progress}
      {flow.error}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          {flow.templateToggle}
          {flow.filesReadyText && (
            <span className="text-xs text-text-tertiary truncate">{flow.filesReadyText}</span>
          )}
        </div>
        {flow.transcribeButton}
      </div>
    </div>
  );

  if (!framed) return body;

  return (
    <Card>
      {title && <CardHeader title={title} />}
      <CardBody>{body}</CardBody>
    </Card>
  );
}

